import {
  Component,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
} from '@angular/core';

import { FormsModule } from '@angular/forms';
import { MediaService } from '../../services/media/media.service';

@Component({
  selector: 'app-media-test',
  standalone: true,
  imports: [FormsModule],
  templateUrl: './media-test.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./media-test.component.scss'],
})
export class MediaTestComponent {
  // Input for obfuscated filename
  obfuscatedFilename: string = '';

  // Upload state
  selectedFile: File | null = null;
  uploadProgress: number = 0;
  uploadedObfuscatedName: string = '';
  isUploading: boolean = false;
  uploadStatusMessage: string = '';

  // Download/View state
  downloadProgress: number = 0;
  isLoading: boolean = false;

  // Media display
  mediaType: 'image' | 'video' | 'other' | null = null;
  mediaUrl: string | null = null;
  mediaMetadata: any = null;

  errorMessage: string = '';
  successMessage: string = '';

  constructor(
    private mediaService: MediaService,
    private cdr: ChangeDetectorRef
  ) {}

  /**
   * Handle file selection for upload
   */
  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.selectedFile = input.files[0];
      this.errorMessage = '';
    }
  }

  /**
   * Upload the selected file
   */
  async uploadFile(): Promise<void> {
    if (!this.selectedFile) {
      this.errorMessage = 'Vælg venligst en fil først';
      this.cdr.detectChanges();
      return;
    }

    this.isUploading = true;
    this.uploadProgress = 0;
    this.errorMessage = '';
    this.successMessage = '';
    this.uploadedObfuscatedName = '';
    this.cdr.detectChanges();

    try {
      const obfuscatedName = await this.mediaService.uploadFile(
        this.selectedFile,
        (progress) => {
          this.uploadProgress = Math.round(progress);
          this.cdr.detectChanges();
        },
        (status) => {
          this.uploadStatusMessage = status;
          this.cdr.detectChanges();
        }
      );

      this.uploadedObfuscatedName = obfuscatedName;
      this.obfuscatedFilename = obfuscatedName; // Auto-fill for viewing
      this.successMessage = 'Fil uploadet og behandlet succesfuldt!';
      this.uploadStatusMessage = '';
      this.cdr.detectChanges();
    } catch (error: any) {
      console.error('Upload error:', error);
      this.errorMessage = `Upload fejlede: ${error.message || 'Ukendt fejl'}`;
      this.cdr.detectChanges();
    } finally {
      this.isUploading = false;
      this.cdr.detectChanges();
    }
  }

  /**
   * Load and display media from obfuscated filename
   */
  async loadMedia(): Promise<void> {
    if (!this.obfuscatedFilename.trim()) {
      this.errorMessage = 'Indtast venligst et obfuskeret filnavn';
      this.cdr.detectChanges();
      return;
    }

    this.isLoading = true;
    this.downloadProgress = 0;
    this.errorMessage = '';
    this.successMessage = '';
    this.clearMedia();
    this.cdr.detectChanges();

    try {

      // Get metadata first
      this.mediaMetadata = await this.mediaService.getMetadata(
        this.obfuscatedFilename
      );

      // Determine media type
      const mimeType = this.mediaMetadata.mime_type;

      if (mimeType.startsWith('image/')) {
        this.mediaType = 'image';
        this.cdr.detectChanges();
        await this.loadImage();
      } else if (mimeType.startsWith('video/')) {
        this.mediaType = 'video';
        this.cdr.detectChanges();
        await this.loadVideo();
      } else {
        this.mediaType = 'other';
        this.cdr.detectChanges();
        await this.downloadOtherFile();
      }

      this.isLoading = false;
      this.cdr.detectChanges();
    } catch (error: any) {
      console.error('Load error:', error);
      this.errorMessage = `Kunne ikke indlæse medie: ${
        error.message || 'Ukendt fejl'
      }`;
      this.isLoading = false;
      this.cdr.detectChanges();
    }
  }

  /**
   * Load and display image
   */
  private async loadImage(): Promise<void> {
    const blob = await this.mediaService.downloadFile(
      this.obfuscatedFilename,
      (progress) => {
        this.downloadProgress = Math.round(progress);
        this.cdr.detectChanges();
      }
    );

    this.mediaUrl = URL.createObjectURL(blob);
    this.cdr.detectChanges();
  }

  /**
   * Load and stream video (with buffering while downloading)
   */
  private async loadVideo(): Promise<void> {

    // Trigger change detection to ensure video element exists
    this.cdr.detectChanges();

    // Find video element
    await new Promise((resolve) => setTimeout(resolve, 100));
    const videoElement = document.querySelector('video') as HTMLVideoElement;

    if (!videoElement) {
      console.error('Video element not found!');
      throw new Error('Video element not found');
    }

    try {
      // Try streaming with MediaSource (plays while downloading)
      await this.mediaService.streamVideo(
        this.obfuscatedFilename,
        videoElement,
        (progress) => {
          this.downloadProgress = Math.round(progress);
          this.cdr.detectChanges();
        }
      );

      // Wait for video to be ready for playback
      return new Promise((resolve) => {
        const onCanPlay = () => {
          videoElement.removeEventListener('canplay', onCanPlay);
          videoElement.removeEventListener('error', onError);
          resolve();
        };

        const onError = (e: Event) => {
          console.error('Video error:', e);
          console.error('Video error details:', {
            error: (videoElement as any).error,
            networkState: videoElement.networkState,
            readyState: videoElement.readyState,
          });
          videoElement.removeEventListener('canplay', onCanPlay);
          videoElement.removeEventListener('error', onError);
          resolve();
        };

        videoElement.addEventListener('canplay', onCanPlay);
        videoElement.addEventListener('error', onError);

        // Try to play immediately (may be blocked by autoplay policy)
        videoElement.play().catch((e) => {
          // Video is still ready, just not playing automatically
        });
      });
    } catch (error: any) {
      console.error('Streaming failed, error:', error);

      // If streaming fails, download entire video
      const blob = await this.mediaService.downloadFile(
        this.obfuscatedFilename,
        (progress) => {
          this.downloadProgress = Math.round(progress);
          this.cdr.detectChanges();
        }
      );

      const blobUrl = URL.createObjectURL(blob);
      videoElement.src = blobUrl;

      // Wait for video to be ready
      return new Promise((resolve) => {
        const onCanPlay = () => {
          videoElement.removeEventListener('canplay', onCanPlay);
          resolve();
        };

        videoElement.addEventListener('canplay', onCanPlay);
        videoElement.load();
      });
    }
  }

  /**
   * Download other file types
   */
  private async downloadOtherFile(): Promise<void> {
    const blob = await this.mediaService.downloadFile(
      this.obfuscatedFilename,
      (progress) => {
        this.downloadProgress = Math.round(progress);
        this.cdr.detectChanges();
      }
    );

    // Trigger download
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = this.mediaMetadata.original_name;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    this.successMessage = `Fil downloadet: ${this.mediaMetadata.original_name}`;
    this.cdr.detectChanges();
  }

  /**
   * Clear displayed media
   */
  clearMedia(): void {
    if (this.mediaUrl) {
      URL.revokeObjectURL(this.mediaUrl);
      this.mediaUrl = null;
    }
    this.mediaType = null;
    this.mediaMetadata = null;
  }

  /**
   * Copy obfuscated name to clipboard
   */
  copyToClipboard(text: string): void {
    navigator.clipboard.writeText(text).then(() => {
      this.successMessage = 'Kopieret til udklipsholder!';
      this.cdr.detectChanges();

      // Clear success message after 3 seconds
      setTimeout(() => {
        this.successMessage = '';
        this.cdr.detectChanges();
      }, 3000);
    });
  }

  /**
   * Get file size in human-readable format
   */
  getFileSize(bytes: number): string {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    if (bytes < 1024 * 1024 * 1024)
      return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
    return (bytes / (1024 * 1024 * 1024)).toFixed(2) + ' GB';
  }
}
